/**
 * Company deposit policy — the ONE place that interprets the deposit_* columns on
 * company_settings (read via the `settings` view / settingsService.getBusinessSettings).
 *
 * Canonical columns (verified live):
 *   deposit_enabled       boolean   — company-wide "collect a deposit by default"
 *   deposit_type          deposit_type_enum — 'PERCENTAGE' | 'FIXED'
 *   deposit_percent       numeric   — nullable, DB default 50.0, NO check constraint
 *   deposit_fixed_amount  numeric   — default 0.00
 *
 * Do NOT confuse these with the PER-QUOTE columns on work_orders
 * (deposit_required / deposit_percentage / deposit_amount). Those are the values actually
 * charged for one quote; the ones here are only the company's default policy used to seed them.
 *
 * Why this file exists: QuoteBuilder and ApprovalModal each interpreted these columns
 * independently and disagreed — a FIXED policy prefilled a dollar amount in one surface while
 * the other seeded a percentage. Every reader must go through here so one setting cannot mean
 * two things (CLAUDE.md §18, one source of truth).
 */

export const DEPOSIT_TYPE = {
  PERCENTAGE: 'PERCENTAGE',
  FIXED: 'FIXED',
};

/** Number or null — never NaN, and never collapses a legitimate 0 the way `||` does. */
const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Normalize a raw settings row into a deposit policy.
 * Safe to call with null/undefined — returns a disabled policy.
 */
export function resolveDepositPolicy(settings) {
  return {
    enabled: !!settings?.deposit_enabled,
    // Anything that is not explicitly FIXED is treated as PERCENTAGE, matching the column default.
    type: settings?.deposit_type === DEPOSIT_TYPE.FIXED ? DEPOSIT_TYPE.FIXED : DEPOSIT_TYPE.PERCENTAGE,
    percent: toNumberOrNull(settings?.deposit_percent),
    fixedAmount: toNumberOrNull(settings?.deposit_fixed_amount),
  };
}

/**
 * The percentage this policy implies, or null when it does not imply one.
 * Returns null for a FIXED policy — a percentage is NOT invented from a dollar amount,
 * because that would depend on a total the policy knows nothing about.
 * Returns null for a non-positive percent: a 0% deposit is not a deposit.
 */
export function depositPercentFromPolicy(policy) {
  if (!policy || policy.type === DEPOSIT_TYPE.FIXED) return null;
  return policy.percent !== null && policy.percent > 0 ? policy.percent : null;
}

/**
 * The deposit amount this policy implies for a given total, or null if it implies none.
 * Rounded to cents. `total` is required for PERCENTAGE and ignored for FIXED.
 */
export function depositAmountFromPolicy(policy, total) {
  if (!policy) return null;

  if (policy.type === DEPOSIT_TYPE.FIXED) {
    return policy.fixedAmount !== null && policy.fixedAmount > 0 ? policy.fixedAmount : null;
  }

  const pct = depositPercentFromPolicy(policy);
  const amount = toNumberOrNull(total);
  if (pct === null || amount === null) return null;
  return Math.round((amount * pct) / 100 * 100) / 100;
}

/**
 * Does this work order's deposit stop it being booked? MIRROR of the database owner
 * private.booking_deposit_check, the ONE deposit rule (Flow 9 Stage 2, PRODUCT_DECISIONS
 * 2026-11-02): blocked only when the quote requires a deposit AND requires it before scheduling
 * AND there is no paid date AND no recorded method (a recorded method counts as paid, on the
 * office side and the customer side alike). A pre-check for screens that book without the
 * booking step (Smart Scheduler crew booking, approve-and-schedule, recurring visit times); the
 * booking step (book_work_order) decides. Change both together — registered as the MIRROR in
 * scripts/.canonical-columns.json (booking-deposit-rule).
 */
export function depositBlocksScheduling(workOrder) {
  return !!(workOrder?.deposit_required && workOrder?.deposit_required_before_scheduling && !workOrder?.deposit_paid_at && !workOrder?.deposit_method);
}
